import React, { useMemo, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import SelectCst from "@components/InputCst/SelectCst";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import { AddItemsSelectionMode, AddItemsTabConfig } from "./types";

const DEFAULT_PAGE_SIZE = 16;

interface SortOption {
  value: string;
  label: string;
}

interface AddItemsTabBodyProps<T> {
  tab: AddItemsTabConfig<T>;
  selectionMode: AddItemsSelectionMode;
  selectedIds: Set<string | number>;
  onToggle: (item: T, next: boolean) => void;
  excludeIds?: (string | number)[];
  sortOptions?: SortOption[];
  sort: string;
  onSortChange: (next: string) => void;
  onCreate?: () => void;
  createLabel?: string;
}

function AddItemsTabBody<T>({
  tab,
  selectionMode,
  selectedIds,
  onToggle,
  excludeIds,
  sortOptions,
  sort,
  onSortChange,
  onCreate,
  createLabel,
}: AddItemsTabBodyProps<T>) {
  const { t } = useTranslation("add-items-modal");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [searchSubmitted, setSearchSubmitted] = useState("");

  const pageSize = tab.pageSize ?? DEFAULT_PAGE_SIZE;
  const filters = tab.useFiltersDefs?.() ?? [];

  const { items, totalItems, totalPages, isLoading } = tab.useDataHook({
    page,
    pageSize,
    search: searchSubmitted || undefined,
    sort: sort || undefined,
  });

  const visibleItems = useMemo(() => {
    if (!excludeIds || excludeIds.length === 0) return items;
    const exclude = new Set(excludeIds.map(String));
    return items.filter((it) => !exclude.has(String(tab.getId(it))));
  }, [items, excludeIds, tab]);

  const handleSearch = (value: string) => {
    setSearchSubmitted(value);
    setPage(1);
  };

  const handleToggle = (id: string | number, next: boolean) => {
    const found = visibleItems.find(
      (it) => String(tab.getId(it)) === String(id),
    );
    if (found) onToggle(found, next);
  };

  const filtersSlot = (
    <>
      {filters.map((f) => (
        <Box
          key={f.id}
          sx={{
            width: 160,
            flexShrink: 0,
            "& .MuiFormControl-root": {
              minWidth: "0 !important",
              width: "100% !important",
            },
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
          }}
        >
          <SelectCst
            id={f.id}
            label={f.label}
            value={f.value}
            options={f.options}
            onChange={(v: any) => {
              f.onChange(String(v ?? ""));
              setPage(1);
            }}
          />
        </Box>
      ))}

      {onCreate && (
        <Box
          component="button"
          type="button"
          onClick={onCreate}
          sx={{
            cursor: "pointer",
            padding: "8px 16px",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: "transparent",
            color: theme.palette.text.primary,
            fontSize: "13px",
            fontWeight: 700,
            fontFamily: "inherit",
            flexShrink: 0,
          }}
        >
          {createLabel ?? String(t("create") ?? "Crea")}
        </Box>
      )}
    </>
  );

  const sortRow = sortOptions && sortOptions.length > 0 && (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Box
        sx={{
          minWidth: 160,
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        <SelectCst
          id={`add-items-sort-${tab.key}`}
          label={String(t("sort.label") ?? "Ordina")}
          value={sort}
          options={sortOptions}
          onChange={(v: any) => onSortChange(String(v ?? ""))}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <SearchableMultiSelectListCst<T>
        selectionMode={selectionMode}
        rowVariant={tab.rowVariant ?? "boxed"}
        items={visibleItems}
        getId={tab.getId}
        renderRow={({ item, selected, onToggle: rowOnToggle }) =>
          tab.renderRow({
            item,
            selected,
            onToggle: rowOnToggle,
            selectionMode,
          })
        }
        selectedIds={selectedIds}
        onToggle={handleToggle}
        initialSearchValue={searchSubmitted}
        onSearchSubmit={handleSearch}
        searchPlaceholder={
          tab.searchPlaceholder ?? String(t("search-placeholder") ?? "Cerca")
        }
        searchButtonLabel={
          tab.searchButtonLabel ?? String(t("search-button") ?? "Cerca")
        }
        filtersSlot={filtersSlot}
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
        isLoading={isLoading}
        showSelectedCount={selectionMode === "multi"}
        selectedCountLabel={String(
          t("selected-count", { count: selectedIds.size }) ??
            `${selectedIds.size} elementi selezionati`,
        )}
        emptyLabel={
          tab.emptyLabel ?? String(t("empty") ?? "Nessun elemento trovato")
        }
      />
      {sortRow ? <Box sx={{ mt: -1 }}>{sortRow}</Box> : null}
    </Box>
  );
}

export default AddItemsTabBody;
